import { Component, Emit, PropSync, Vue, Watch } from 'nuxt-property-decorator';
import { cloneDeep, concat, isEqual, set } from 'lodash';
import { gql } from 'graphql-tag';
import {
  PurchaseOrderStatus,
  PurchaseProductPurchaseType,
  PurchaseProductQueriesFindAllArgs,
  Query
} from '~/schema';

/**
 * Subscription Filter Modal Component
 *
 * @export
 * @class SubscriptionFilterModalComponent
 * @extends {Vue}
 */
@Component({})
export default class SubscriptionFilterModalComponent extends Vue {
  @PropSync('isOpen', { type: Boolean, default: false }) modalIsOpen!: boolean;

  @Emit('close')
  private emitClose(): void {}

  @Emit('fetch')
  private emitFetch(): void {}

  protected loading: {
    fetchSubscriptionProducts: boolean;
  } = {
    fetchSubscriptionProducts: false
  };

  protected filter = {
    paymentStatus: [],
    productIds: [],
    source: [],
    kitDelivery: undefined,
    orderDate: [],
    paymentDate: [],
    subsStartDate: [] as Array<string>,
    subsEndDate: [] as Array<string>
  };

  protected filterSearch = {
    subscriptionName: ''
  };

  protected subscription: {
    items: Array<{
      id: string;
      name: string;
    }>;
    total: number;
  } = {
    items: [],
    total: 0
  };

  protected query: {
    subscription: PurchaseProductQueriesFindAllArgs;
  } = {
    subscription: {
      where: {
        title: '',
        purchaseType: [PurchaseProductPurchaseType.Subscription]
      },
      limit: 30,
      offset: 0
    }
  };

  protected timeout = {
    search: null as NodeJS.Timeout | null
  };

  protected orderDateMenu: boolean = false;

  protected paymentDateMenu: boolean = false;

  protected subsStartDateMenu: boolean = false;

  protected subsEndDateMenu: boolean = false;

  protected paymentStatusOptions: Array<{
    text: string;
    value: string;
  }> = [
    {
      text: 'Payment Expired',
      value: PurchaseOrderStatus.PaymentExpired
    },
    {
      text: 'Waiting for Payment',
      value: PurchaseOrderStatus.WaitingForPayment
    },
    {
      text: 'Payment Received',
      value: PurchaseOrderStatus.PaymentReceived
    },
    {
      text: 'Payment Cancelled',
      value: PurchaseOrderStatus.Cancelled
    }
  ];

  protected subscriptionNameOptions: Array<{
    text: string;
    value: string;
  }> = [];

  protected sourceOptions: Array<{
    text: string;
    value: string;
  }> = [
    {
      text: 'Dashboard',
      value: 'DASHBOARD'
    },
    {
      text: 'Apps',
      value: 'APPS'
    }
  ];

  protected kitDeliveryOptions: Array<{
    text: string;
    value: string;
  }> = [
    {
      text: 'Yes',
      value: 'YES'
    },
    {
      text: 'No',
      value: 'NO'
    },
    {
      text: 'All',
      value: 'ALL'
    }
  ];

  mounted(): void {
    this.restoreFromStore();
    this.fetchSubscriptionProducts();
  }

  protected changeDateOrder(date: Array<string>): void {
    this.filter.orderDate = date;
  }

  protected changeDatePayment(date: Array<string>): void {
    this.filter.paymentDate = date;
  }

  @Watch('filterSearch.subscriptionName', { deep: true })
  onFilterSubscriptionNameChange(name: string | null): void {
    if (this.timeout.search) {
      clearTimeout(this.timeout.search);
    }
    this.timeout.search = setTimeout(() => {
      set(this.query, 'subscription.where.title', name ?? '');
      this.refetchSubscriptionProducts();
    }, 500);
  }

  private refetchSubscriptionProducts(): void {
    this.subscription.items = [];
    this.subscription.total = 0;
    this.query.subscription.offset = 0;
    this.fetchSubscriptionProducts();
  }

  protected async fetchSubscriptionProducts(): Promise<void> {
    if (this.loading.fetchSubscriptionProducts) return;

    try {
      this.loading.fetchSubscriptionProducts = true;

      const query = gql`
        query findAll(
          $where: PurchaseProductQueryFilter
          $sort: PurchaseProductQuerySortBy
          $limit: Int
          $offset: Int
          $rounded: Boolean
        ) {
          Purchase {
            id
            Product {
              findAll(
                where: $where
                sort: $sort
                limit: $limit
                offset: $offset
              ) {
                count
                results {
                  id
                  code
                  title
                  price
                  purchaseType
                  image
                  imageApp
                  imageWeb
                  types
                  images {
                    id
                    pointer
                    position
                    __typename
                  }
                  showOnShop
                  description
                  createdAt
                  discount {
                    id
                    amount
                    percentage(rounded: $rounded)
                    __typename
                  }
                  order
                  merchantType
                  productGroup {
                    id
                    name
                    __typename
                  }
                  isUpsell
                }
              }
            }
          }
        }
      `;

      const variables: PurchaseProductQueriesFindAllArgs = {
        ...this.query.subscription
      };
      const response = await this.$api.query<Query>({
        query,
        variables
      });
      const data = response.data?.Purchase?.Product?.findAll;
      const results = data.results ?? [];

      this.subscription.total = data?.count ?? 0;
      this.subscription.items = concat(
        this.subscription.items,
        results.map((item) => ({
          id: item.id,
          name: item.title
        }))
      );
    } catch (error) {
      this.$emitter.emit('TOAST', error);
    } finally {
      this.loading.fetchSubscriptionProducts = false;
    }
  }

  protected handleSubscriptionProductsIntersect(
    ...params: [any, any, boolean]
  ): void {
    const isIntersecting = params[2];
    if (isIntersecting) {
      if (
        this.subscription.items?.length < this.subscription.total &&
        !this.loading.fetchSubscriptionProducts
      ) {
        const page = Math.floor(
          this.query.subscription.offset / this.query.subscription.limit
        );
        this.query.subscription.offset =
          (page + 1) * this.query.subscription.limit;
        this.fetchSubscriptionProducts();
      }
    }
  }

  protected changeDateSubsStart(date: Array<string>): void {
    this.filter.subsStartDate = date;
  }

  protected changeDateSubsEnd(date: Array<string>): void {
    this.filter.subsEndDate = date;
  }

  protected restoreFromStore(): void {
    const storeFilter = cloneDeep(
      this.$store.state.order.subscription.table.filter
    );
    if (storeFilter) {
      this.filter = cloneDeep({
        paymentStatus: [...storeFilter.paymentStatus],
        productIds: [...storeFilter.productIds],
        source: [...storeFilter.source],
        kitDelivery: storeFilter.kitDelivery,
        orderDate: [...storeFilter.orderDate],
        paymentDate: [...storeFilter.paymentDate],
        subsStartDate: storeFilter.subsStartDate,
        subsEndDate: storeFilter.subsEndDate
      });
    }
  }

  protected save(): void {
    const currentStoreFilter =
      this.$store.state.order.subscription.table.filter;

    // Compare current filter with store filter
    if (!isEqual(this.filter, currentStoreFilter)) {
      // Only commit if there are changes
      this.$store.commit('order/changeSubscriptionTableFilter', this.filter);
    }

    this.emitFetch();
    this.emitClose();
  }
}
